import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";

/** Cancelled booking row from /api/bookings/cancelled (returns {results}). */
export interface Cancellation {
  id: string | number;
  quest_id: string;
  date: string;
  time: string;
  custom_time?: string | null;
  short_name?: string | null;
  quest_name?: string | null;
  /** Название спецверсии — именно оно называет игру (22.09, владелец). */
  special_version_title?: string | null;
  branch_name?: string | null;
  client_name?: string | null;
  phone?: string | null;
  players?: number | null;
  source?: string | null;
  /** booking | lead | block — в отменах лежат вперемешку, отсюда фильтр по типу. */
  type?: string | null;
  cancel_reason?: string | null;
  block_reason?: string | null;   // 28.09: у снятого блока — зачем он стоял
  cancelled_by_name?: string | null;
  cancelled_at?: string | null;
}

interface ListResp { results?: Cancellation[] }

/** Что мерит календарь: дату игры (как было) или дату самой отмены (25.09, владелец). */
export type CancelBy = "game" | "cancelled";

/** 11.09 (Даша через владельца): отмены фильтруются периодом из календаря шапки. */
export function useCancellations(from?: string | null, to?: string | null, by: CancelBy = "game") {
  const [items, setItems] = useState<Cancellation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    if (by === "cancelled") qs.set("by", "cancelled");
    api<ListResp>(`/api/bookings/cancelled${qs.toString() ? `?${qs}` : ""}`)
      .then((r) => {
        if (!alive) return;
        setItems(r.results ?? []);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить отмены");
        setLoading(false);
      });
    return () => { alive = false; };
  }, [trigger, from, to, by]);

  /** Restore a cancelled booking to its original slot. */
  const restore = useCallback(async (c: Cancellation): Promise<{ ok: boolean; error?: string }> => {
    try {
      await api("/api/bookings/restore", {
        method: "PUT",
        body: { id: c.id, quest_id: c.quest_id, date: c.date, time: c.custom_time || c.time },
      });
      reload();
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Не удалось вернуть" };
    }
  }, [reload]);

  return { items, loading, error, reload, restore };
}
